import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api.ts';

/**
 * "แจ้งเตือนตรงนี้ให้มีแจ้งเตือนที่ระบบแชร์โพสต์" (owner, 30 ก.ย. 2569).
 *
 * The team-post share fires by itself — when the list really changed, or once a run of edits has piled up — so
 * until now the shop only learned of it by going to look at the Page. These are the shares of the last week,
 * read from the audit rows the sync already writes.
 */
export interface RecentShare {
  id: string;
  at: string;
  month: string | null;
  monthLabelTh: string | null;
  /** A batched share carries the run of edits that set it off; a plain one followed a single change. */
  batch: boolean;
  changes: number | null;
  teams: number | null;
  permalink: string | null;
  /** Why it did not go out, when it did not. */
  error?: string | null;
  failedStep?: 'write' | 'share' | null;
}

/** When the shop last opened the bell, so a share it has already seen stops asking for attention. */
const SEEN_KEY = 'natashu.shares.seenAt';

function readSeen(): number {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    const n = raw ? Date.parse(raw) : Number.NaN;
    return Number.isNaN(n) ? 0 : n;
  } catch {
    // Private windows and blocked site data: treat every share as already seen rather than nagging forever.
    return Date.now();
  }
}

export function useRecentShares(enabled: boolean) {
  const query = useQuery({
    queryKey: ['team-posts', 'shares'] as const,
    queryFn: ({ signal }) => api.get<{ items: RecentShare[] }>('/api/team-posts/shares', { signal }),
    enabled,
    staleTime: 60_000,
    refetchInterval: 300_000,
  });

  const [seenAt, setSeenAt] = useState(readSeen);
  useEffect(() => {
    if (!enabled) return;
    setSeenAt(readSeen());
  }, [enabled]);

  /**
   * What the bell lists: only shares the shop has not already looked at (owner, 5 ต.ค. 2569: "แจ้งเตือนเมื่อกดอ่านแล้วก็หายไปสิ").
   * Opening the bell moves this line up to where the previous look ended, so what was just read stays on screen
   * while the bell is open and is gone the next time it is opened.
   */
  const [listedFrom, setListedFrom] = useState(readSeen);
  useEffect(() => {
    if (enabled) setListedFrom(readSeen());
  }, [enabled]);
  const allItems = query.data?.items ?? [];
  const items = allItems.filter((s) => Date.parse(s.at) > listedFrom);
  const unseen = allItems.filter((s) => Date.parse(s.at) > seenAt).length;

  /** Called when the bell is opened: everything listed there has now been seen. */
  const markSeen = useCallback(() => {
    setListedFrom(readSeen());
    const now = new Date().toISOString();
    try {
      window.localStorage.setItem(SEEN_KEY, now);
    } catch {
      // Nothing to do — the dot simply comes back next time, which is the harmless direction to fail in.
    }
    setSeenAt(Date.parse(now));
  }, []);

  return { items, unseen, markSeen };
}
